'use client'

import { useState } from 'react'

import { ChevronDown } from 'lucide-react'

import { CurrencyCarryingHtml } from '@/lib/currency/currency-carrying-html'
import { sanitizeHtml } from '@/lib/sanitize-html'
import { cn } from '@/lib/utils'

/**
 * One carrier's expandable "about" card. A client component because the expand
 * toggle is interactive; scoped to just this card so the rest of the carrier
 * surface stays server-rendered.
 *
 * Its props are deliberately narrower than the carrier record it renders from.
 * Whatever a client component receives is serialized into the flight payload, so
 * taking just this card's own content keeps the record's `guideUrl` — a
 * locale-agnostic path — on the server, where the table's `Link` resolves it.
 */
interface CarrierAccordionProps {
    name: string
    logoUrl: string | null
    /** Carrier bio as HTML; sanitized here before it is written to the DOM. */
    aboutHtml: string | null
    images: { src: string; alt: string }[]
}

export function CarrierAccordion({ name, logoUrl, aboutHtml, images }: CarrierAccordionProps) {
    const [open, setOpen] = useState(false)

    if (!aboutHtml) {
        return null
    }

    return (
        <li className="border border-[#d9dee5] first:rounded-t-[7px] last:rounded-b-[7px] [&:not(:first-child)]:-mt-px">
            <button
                type="button"
                className="flex w-full items-center gap-3 px-4 py-4"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
            >
                {logoUrl && (
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={logoUrl} alt="" className="h-10 w-10 object-contain" />
                    </span>
                )}
                <span className="text-base text-[#44474a]">{name}</span>
                <ChevronDown
                    className={cn(
                        'ml-auto h-6 w-6 shrink-0 text-[#44474a] transition-transform duration-200',
                        open && 'rotate-180',
                    )}
                />
            </button>
            <div
                className={cn(
                    'grid transition-[grid-template-rows] duration-200 ease-in-out',
                    open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                )}
            >
                <div className="overflow-hidden">
                    <div className="px-4 pb-4">
                        <CurrencyCarryingHtml
                            className="prose max-w-none text-base leading-6 text-[#44474a] [&_a]:text-[#00bfd5] [&_a:hover]:text-[#008a99]"
                            html={sanitizeHtml(aboutHtml)}
                        />
                        {images.length > 0 && (
                            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                                {images.map((img) => (
                                    // eslint-disable-next-line @next/next/no-img-element
                                    <img
                                        key={img.src}
                                        src={img.src}
                                        alt={img.alt}
                                        className="h-auto w-full rounded object-cover"
                                        loading="lazy"
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </li>
    )
}
